'use client'

import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label} from '@headlessui/react'
import {CheckIcon, ChevronUpDownIcon} from '@heroicons/react/20/solid'
import {useState} from 'react'
import {XMarkIcon} from "@heroicons/react/24/outline";

interface ComboboxOption {
    displayName: string,
    id: string,
}

type MapperFn<FromT, ToT> = (from: FromT) => ToT;

function transform_all<FromT, ToT>(from: FromT[], mapping: MapperFn<FromT, ToT>): ToT[] {
    return from.map(mapping)
}

interface Props<OptionT> {
    label?: string,
    options: OptionT[]
    onSelect: (selected: (ComboboxOption & Partial<OptionT>) | null) => void,
    selected: OptionT | null,
    emptySelectionMessage: string,
    disabled?: boolean,
    internalMapper: MapperFn<OptionT, ComboboxOption & OptionT>
}

function getAvailableOptions<OptionT>(options: (ComboboxOption & OptionT)[], query: string): (ComboboxOption & OptionT)[] {
    return query === ''
        ? options
        : options.filter((option) => {
            return option.displayName.toLowerCase().includes(query.toLowerCase())
        })
}

export function FreeformComboBoxList<OptionT>({
                                                  label,
                                                  options,
                                                  onSelect,
                                                  selected,
                                                  emptySelectionMessage,
                                                  internalMapper,
                                                  disabled
                                              }: Props<OptionT>) {

    const comboboxOptions = transform_all<OptionT, ComboboxOption & OptionT>(options, internalMapper)
    const [query, setQuery] = useState<string | null>(null)

    const filteredOptions = getAvailableOptions(comboboxOptions, query ?? '')

    let selectedComboboxOption: (ComboboxOption & Partial<OptionT>) | null;

    if (selected !== null && selected !== undefined)
        selectedComboboxOption = internalMapper(selected as OptionT)
    else
        selectedComboboxOption = null

    const hasNoMatches = filteredOptions.length === 0 && (query ?? '').trim().length > 0
    const customOption: ComboboxOption = {displayName: query ?? '', id: `__custom__:${query ?? ''}`}
    const displayText = query ?? selectedComboboxOption?.displayName ?? ''

    return (
        <div className="max-w-96">

            <Combobox
                as="div"
                disabled={disabled}
                value={selectedComboboxOption}
                onChange={(option: (ComboboxOption & Partial<OptionT>) | null) => {
                    setQuery(null)
                    onSelect(option)
                }}
            >
                <Label className="block text-sm/6 font-medium text-gray-900">{label}</Label>
                <div className="relative mt-2">
                    <ComboboxInput
                        placeholder={emptySelectionMessage}
                        className="block w-full text-ellipsis disabled:opacity-75 disabled:text-gray-500 rounded-md bg-white shadow-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 outline-none py-1.5 pr-20 pl-3 text-base text-gray-900 outline-1 placeholder:text-gray-400 sm:text-sm/6"
                        onChange={(event) => setQuery(event.target.value)}
                        onBlur={() => setQuery(null)}
                        title={displayText}
                        value={displayText}

                        displayValue={(value: (ComboboxOption & Partial<OptionT>) | null) =>
                            value?.displayName ?? ''
                        }
                    />
                    <ComboboxButton
                        className="disabled:hidden absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
                        <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true"/>
                    </ComboboxButton>
                    {!disabled && <button
                        type="button"
                        onClick={(e) => {
                            onSelect(null);
                            e.preventDefault();
                        }}
                        className="absolute inset-y-0 right-6 flex items-center rounded-r-md px-2 focus:outline-hidden">
                        <XMarkIcon className="size-5 text-gray-400 bg-white" aria-hidden="true"/>
                    </button>}

                    {filteredOptions.length > 0 && (
                        <ComboboxOptions
                            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
                            {filteredOptions.map((option) => (
                                <ComboboxOption
                                    key={option.id}
                                    value={option}
                                    className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-primary data-focus:text-white data-focus:outline-hidden"
                                >
                                <span
                                    className="block truncate group-data-selected:font-semibold">{option.displayName}</span>

                                    <span
                                        className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary group-data-focus:text-white group-data-selected:flex">
                  <CheckIcon className="size-5" aria-hidden="true"/>
                </span>
                                </ComboboxOption>
                            ))}

                        </ComboboxOptions>
                    )}
                    {hasNoMatches && (
                        <ComboboxOptions
                            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">

                            <ComboboxOption
                                key={customOption.id}
                                disabled={false}
                                className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-primary data-focus:text-white data-focus:outline-hidden"
                                value={customOption}>
                                <span className="block truncate group-data-selected:font-semibold">{query}</span>
                                <span
                                    className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary group-data-focus:text-white group-data-selected:flex"></span>
                            </ComboboxOption>


                        </ComboboxOptions>
                    )}
                </div>
            </Combobox>
        </div>

    )
}
